import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Send, Smile, AtSign, Paperclip } from 'lucide-react';
import { TagInput } from './TagInput';
import { MentionPicker } from './MentionPicker';
import { AttachmentUploader } from './AttachmentUploader';
// Curated emoji grid. Mirrored from the pill version; keep them in sync if extending.
const EMOJI_PALETTE = [
    '😀', '😄', '😅', '😂', '🙂', '😉', '😊', '😍',
    '🤔', '😎', '😴', '😬', '🤯', '😭', '😡', '🥲',
    '👍', '👎', '👌', '🙌', '👏', '🙏', '💪', '👀',
    '🔥', '⭐', '✨', '💡', '✅', '❌', '⚠️', '❗',
    '📍', '🗺️', '🏞️', '🌲', '🌊', '🏔️', '🚧', '🛠️',
    '📝', '📷', '🔍', '⏱️', '🚀', '🎯', '💬', '🤝',
];
// Compact "drop and write" composer: auto-growing single-line input + circular
// send button, optional always-visible tag chip input, and a tools row
// (emoji / @-mention / attach). Used by both the map draft pill and the
// reply/edit surfaces inside CommentItem.
export const CompactCommentComposer = ({ initialContent = '', initialTags = [], placeholder = 'Add a comment...', submitLabel = 'Post', autoFocus = false, onSubmit, onCancel, isReply = false, isEditing = false, anchor, preLinkedArtifactId, inheritedArtifactIds = [], collaborators = [], tagSuggestions = [], hideTagInput = false, showAttachments = true, }) => {
    const textareaRef = useRef(null);
    const mentionStartRef = useRef(null);
    const [content, setContent] = useState(initialContent);
    const [tags, setTags] = useState(initialTags);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [mentionedUserIds, setMentionedUserIds] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionAnchor, setMentionAnchor] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        var _a;
        if (autoFocus)
            (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [autoFocus]);
    // Auto-grow up to ~5 lines worth.
    useEffect(() => {
        if (!textareaRef.current)
            return;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }, [content]);
    const hasContent = content.trim().length > 0;
    const hasLinkedArtifacts = inheritedArtifactIds.length > 0 || !!preLinkedArtifactId;
    const hasAnchor = !!anchor;
    const canSubmit = hasContent
        && (isEditing || isReply || hasLinkedArtifacts || hasAnchor)
        && !isSubmitting;
    // Mention detection — walks back from the caret to find an '@' at a word boundary.
    const detectMentionTrigger = useCallback((value, cursorPos) => {
        if (collaborators.length === 0) {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        let i = cursorPos - 1;
        while (i >= 0) {
            const ch = value[i];
            if (ch === '@')
                break;
            if (ch === ' ' || ch === '\n' || ch === '\t') {
                setMentionQuery(null);
                mentionStartRef.current = null;
                return;
            }
            i--;
        }
        if (i < 0) {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        const prev = i > 0 ? value[i - 1] : '';
        if (prev && prev !== ' ' && prev !== '\n' && prev !== '\t') {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        mentionStartRef.current = i;
        setMentionQuery(value.slice(i + 1, cursorPos));
        if (textareaRef.current) {
            const rect = textareaRef.current.getBoundingClientRect();
            setMentionAnchor({ x: rect.left, y: rect.bottom + 4 });
        }
    }, [collaborators.length]);
    const handleContentChange = (e) => {
        var _a;
        const value = e.target.value;
        setContent(value);
        detectMentionTrigger(value, (_a = e.target.selectionStart) !== null && _a !== void 0 ? _a : value.length);
    };
    const handlePickMention = (user) => {
        var _a, _b;
        if (mentionStartRef.current === null)
            return;
        const start = mentionStartRef.current;
        const cursorPos = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) !== null && _b !== void 0 ? _b : content.length;
        const displayName = (user.firstName || user.lastName)
            ? `${user.firstName} ${user.lastName}`.trim()
            : user.email;
        const before = content.slice(0, start);
        const after = content.slice(cursorPos);
        const next = `${before}@${displayName} ${after}`;
        setContent(next);
        if (!mentionedUserIds.includes(user.userId)) {
            setMentionedUserIds(prev => [...prev, user.userId]);
        }
        setMentionQuery(null);
        mentionStartRef.current = null;
        setTimeout(() => {
            if (textareaRef.current) {
                const caret = before.length + displayName.length + 2;
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(caret, caret);
            }
        }, 0);
    };
    const insertAtCursor = (toInsert) => {
        var _a, _b;
        const ta = textareaRef.current;
        if (!ta)
            return;
        const start = (_a = ta.selectionStart) !== null && _a !== void 0 ? _a : content.length;
        const end = (_b = ta.selectionEnd) !== null && _b !== void 0 ? _b : content.length;
        const next = content.slice(0, start) + toInsert + content.slice(end);
        setContent(next);
        setTimeout(() => {
            ta.focus();
            const caret = start + toInsert.length;
            ta.setSelectionRange(caret, caret);
            detectMentionTrigger(next, caret);
        }, 0);
    };
    const handleSubmit = async () => {
        if (!canSubmit)
            return;
        setIsSubmitting(true);
        try {
            const validAttachments = pendingAttachments.filter(a => !a.error).map(a => a.file);
            // Combine inherited (parent thread) + pre-linked (clicked feature) artifact IDs,
            // dedup'd. Either may be empty.
            const artifactIds = Array.from(new Set([
                ...inheritedArtifactIds,
                ...(preLinkedArtifactId ? [preLinkedArtifactId] : []),
            ]));
            await onSubmit({
                content: content.trim(),
                attachments: validAttachments,
                artifactIds,
                mentionedUserIds,
                anchor,
                tags,
            });
            pendingAttachments.forEach(a => { if (a.previewUrl)
                URL.revokeObjectURL(a.previewUrl); });
            setContent('');
            setPendingAttachments([]);
            setMentionedUserIds([]);
            setTags(initialTags);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (canSubmit)
                handleSubmit();
        }
        if (e.key === 'Escape' && onCancel) {
            e.preventDefault();
            onCancel();
        }
    };
    // Emoji picker
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiBtnRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const [emojiPickerPos, setEmojiPickerPos] = useState(null);
    useEffect(() => {
        if (!showEmojiPicker)
            return;
        const onClickOutside = (e) => {
            var _a, _b;
            const target = e.target;
            if ((_a = emojiPickerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if ((_b = emojiBtnRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))
                return;
            setShowEmojiPicker(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [showEmojiPicker]);
    const onEmojiBtnClick = () => {
        if (!showEmojiPicker && emojiBtnRef.current) {
            const rect = emojiBtnRef.current.getBoundingClientRect();
            setEmojiPickerPos({ x: rect.left, y: rect.top });
        }
        setShowEmojiPicker(s => !s);
    };
    const pickEmoji = (emoji) => {
        insertAtCursor(emoji);
        setShowEmojiPicker(false);
    };
    // Attach
    const fileInputRef = useRef(null);
    const onAttachClick = () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); };
    const onFilesPicked = (e) => {
        var _a;
        const files = Array.from((_a = e.target.files) !== null && _a !== void 0 ? _a : []);
        if (files.length === 0)
            return;
        const newAttachments = files.map(file => ({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
            status: 'pending',
            progress: 0,
        }));
        setPendingAttachments(prev => [...prev, ...newAttachments]);
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    };
    const hasAttachments = pendingAttachments.length > 0;
    const sendBg = useMemo(() => (canSubmit ? 'var(--fern)' : 'var(--pewter)'), [canSubmit]);
    return (_jsxs("div", { className: "flex flex-col", children: [_jsxs("div", { className: "flex items-end gap-2", children: [_jsx("textarea", { ref: textareaRef, value: content, onChange: handleContentChange, onKeyUp: (e) => { var _a; return detectMentionTrigger(e.target.value, (_a = e.target.selectionStart) !== null && _a !== void 0 ? _a : 0); }, onClick: (e) => { var _a; return detectMentionTrigger(e.currentTarget.value, (_a = e.currentTarget.selectionStart) !== null && _a !== void 0 ? _a : 0); }, onKeyDown: handleKeyDown, placeholder: placeholder, disabled: isSubmitting, rows: 1, className: "flex-1 resize-none bg-transparent px-2 py-1.5 text-sm outline-none rounded", style: {
                            color: 'var(--sand)',
                            border: '1px solid var(--pewter)',
                            minHeight: '32px',
                            maxHeight: '120px',
                        } }), _jsx("button", { onClick: handleSubmit, disabled: !canSubmit, title: canSubmit ? `${submitLabel} (⌘⏎)` : 'Type a comment to submit', className: "flex items-center justify-center rounded-full transition-colors", style: {
                            width: 32,
                            height: 32,
                            backgroundColor: sendBg,
                            color: canSubmit ? 'white' : 'var(--dew)',
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            flexShrink: 0,
                        }, children: _jsx(Send, { size: 14 }) })] }), mentionQuery !== null && mentionAnchor && (_jsx(MentionPicker, { query: mentionQuery, collaborators: collaborators, anchor: mentionAnchor, onPick: handlePickMention, onClose: () => {
                    setMentionQuery(null);
                    mentionStartRef.current = null;
                } })), !hideTagInput && (_jsx("div", { className: "pt-2", children: _jsx(TagInput, { value: tags, onChange: setTags, suggestions: tagSuggestions, disabled: isSubmitting }) })), _jsxs("div", { className: "flex items-center justify-between pt-1.5", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { ref: emojiBtnRef, onClick: onEmojiBtnClick, disabled: isSubmitting, title: "Insert emoji", className: "p-1 rounded hover:bg-white/10 transition-colors", style: { color: showEmojiPicker ? 'var(--fern)' : 'var(--dew)' }, children: _jsx(Smile, { size: 14 }) }), _jsx("button", { onClick: () => insertAtCursor('@'), disabled: isSubmitting || collaborators.length === 0, title: "Mention someone", className: "p-1 rounded hover:bg-white/10 transition-colors", style: { color: 'var(--dew)' }, children: _jsx(AtSign, { size: 14 }) }), showAttachments && (_jsxs(_Fragment, { children: [_jsx("button", { onClick: onAttachClick, disabled: isSubmitting, title: "Attach file", className: "p-1 rounded hover:bg-white/10 transition-colors", style: { color: 'var(--dew)' }, children: _jsx(Paperclip, { size: 14 }) }), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".jpg,.jpeg,.png,.gif,.webp,.pdf", onChange: onFilesPicked, style: { display: 'none' } })] }))] }), onCancel && (_jsxs("button", { onClick: onCancel, disabled: isSubmitting, className: "flex items-center gap-1.5 px-2 py-0.5 rounded text-xs hover:bg-white/10 transition-colors", style: { color: 'var(--dew)' }, children: ["Cancel", _jsx("kbd", { className: "text-[10px] px-1 py-0.5 rounded", style: { backgroundColor: 'rgba(255,255,255,0.1)' }, children: "Esc" })] }))] }), showAttachments && hasAttachments && (_jsx("div", { className: "pt-2", children: _jsx(AttachmentUploader, { pendingAttachments: pendingAttachments, onAttachmentsChange: setPendingAttachments, disabled: isSubmitting }) })), showEmojiPicker && emojiPickerPos && createPortal(_jsx("div", { ref: emojiPickerRef, className: "fixed rounded-lg shadow-xl p-2", style: {
                    left: emojiPickerPos.x,
                    bottom: window.innerHeight - emojiPickerPos.y + 4,
                    width: 232,
                    backgroundColor: 'var(--charcoal)',
                    border: '1px solid var(--pewter)',
                    zIndex: 9999,
                }, children: _jsx("div", { className: "grid gap-1", style: { gridTemplateColumns: 'repeat(8, 1fr)' }, children: EMOJI_PALETTE.map((emoji) => (_jsx("button", { onClick: () => pickEmoji(emoji), className: "rounded hover:bg-white/10 transition-colors flex items-center justify-center", style: { width: 24, height: 24, fontSize: 16, lineHeight: 1 }, title: emoji, children: emoji }, emoji))) }) }), document.body)] }));
};
